/**
 * EditableField — tap-to-edit primitive (Block 6.B.1 / ADR-024
 * D-edit-affordance + D-validation + D-keyboard-conventions).
 *
 * Visual cue (D-edit-affordance / Q1 disposition): dotted underline
 * always visible on read view. Tap morphs to input/textarea; blur
 * commits save (D-keyboard-conventions: Enter blurs single-line; Esc
 * reverts; Cmd/Ctrl+Enter blurs multiline).
 *
 * Validation (D-validation): caller supplies a Validator. On blur,
 * validate first; if invalid, show inline error and DO NOT fire
 * onSave. User corrects + blurs again, OR presses Esc to revert.
 * Submit is never gated (per ADR-017 F10).
 *
 * F1: no modal layer. F3: no per-field save button. F4: dotted
 * underline is always visible (not hover-only). F8: 375px-first.
 *
 * Accessibility:
 *   - Read-mode is a button (role=button); keyboard activatable via
 *     Enter / Space.
 *   - Edit-mode input has aria-label from `label` prop.
 *   - aria-invalid + aria-describedby wired on validation failure.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { noopValidator, type Validator } from '@/lib/validators';
import { SavingIndicator } from './SavingIndicator';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface EditableFieldProps {
  /** Current saved value; rendered as text in read mode. */
  value: string | null;
  /** Saves the new value. Should resolve on success / reject on failure. */
  onSave: (newValue: string, priorValue: string) => Promise<void>;
  /** ARIA label / placeholder hint for the input. */
  label: string;
  /** Optional validator. Default: noopValidator. */
  validate?: Validator;
  /** Multi-line (textarea) instead of single-line (input). */
  multiline?: boolean;
  /** Placeholder shown in read mode when value is null/empty. */
  placeholder?: string;
  /** Display formatter for read mode (e.g. raw "yyyy-MM-dd" → "Apr 15, 2026"). */
  format?: (value: string) => string;
  /**
   * Id on the read-mode button (and the edit-mode input/textarea). A
   * caller that needs to reference this control's own accessible name
   * directly, e.g. DirectoryBand's h1 aria-labelledby, which must name
   * the actual control rather than recurse into its content, supplies
   * a stable id here.
   */
  id?: string;
  /**
   * Phase 1.11 — read-only render when false. Defaults to true. Consumer
   * gates on `canDirectWrite(whoami)` at the call site. When false, the
   * field renders the value (or placeholder) as plain text with no
   * click-to-edit affordance.
   */
  canEdit?: boolean;
}

const SAVED_INDICATOR_MS = 1500;

export function EditableField({
  value,
  onSave,
  label,
  validate = noopValidator,
  multiline = false,
  placeholder,
  format,
  canEdit = true,
  id,
}: EditableFieldProps): ReactNode {
  // Phase 1.11 — read-only render path. Skips ALL editing-state UI and
  // affordances; just renders the formatted value or the placeholder
  // text in muted style. Identity-preserving on the displayed value with
  // the canEdit=true read-mode path.
  if (!canEdit) {
    const formatted =
      value !== null && value !== ''
        ? format
          ? format(value)
          : value
        : null;
    if (formatted === null) {
      return <span className="text-ink-soft italic text-sm">{placeholder ?? '–'}</span>;
    }
    return <span className="text-sm text-ink">{formatted}</span>;
  }

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'saving' | 'saved' | 'error' | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();
  const errorId = useId();
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When value changes externally (e.g. cache invalidation), refresh
  // draft if user isn't actively editing.
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  // Cleanup the "saved" indicator timer.
  useEffect(() => {
    return () => {
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    };
  }, []);

  function enterEditMode(): void {
    setDraft(value ?? '');
    setValidationError(null);
    setSaveState(null);
    setSaveError(null);
    setEditing(true);
  }

  function revert(): void {
    setDraft(value ?? '');
    setValidationError(null);
    setEditing(false);
  }

  async function commitIfChanged(): Promise<void> {
    const trimmed = draft;
    const prior = value ?? '';
    if (trimmed === prior) {
      // No change; drop edit mode silently.
      setEditing(false);
      return;
    }
    const err = validate(trimmed);
    if (err !== null) {
      setValidationError(err);
      // Stay in edit mode so user can correct.
      return;
    }
    setValidationError(null);
    setSaveState('saving');
    setSaveError(null);
    try {
      await onSave(trimmed, prior);
      setSaveState('saved');
      setEditing(false);
      // Auto-clear "saved" indicator after SAVED_INDICATOR_MS.
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        setSaveState(null);
      }, SAVED_INDICATOR_MS);
    } catch (e) {
      setSaveState('error');
      setSaveError(pageWriteText(e, contact));
      // Stay in edit mode so user sees the failure + can retry.
    }
  }

  function handleKeyDownInput(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commitIfChanged();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      revert();
    }
  }

  function handleKeyDownTextarea(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void commitIfChanged();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      revert();
    }
  }

  if (editing) {
    const sharedProps = {
      id,
      'aria-label': label,
      'aria-invalid': validationError !== null,
      'aria-describedby': validationError !== null ? errorId : undefined,
      autoFocus: true,
      value: draft,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setDraft(e.target.value),
      onBlur: () => void commitIfChanged(),
      className:
        'w-full px-2 py-1 border rounded text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
        (validationError !== null
          ? 'border-red-500 ring-1 ring-red-200'
          : 'border-sage-400'),
    } as const;
    return (
      <span className="inline-block w-full">
        {multiline ? (
          <textarea
            {...sharedProps}
            rows={3}
            onKeyDown={handleKeyDownTextarea}
          />
        ) : (
          <input
            {...sharedProps}
            type="text"
            onKeyDown={handleKeyDownInput}
          />
        )}
        <SavingIndicator
          state={saveState}
          errorMessage={saveError ?? undefined}
        />
        {validationError !== null && (
          <span
            id={errorId}
            role="alert"
            className="block text-xs text-red-700 mt-1"
          >
            ⚠ {validationError}
          </span>
        )}
      </span>
    );
  }

  // Read mode.
  const displayValue = value === null || value === ''
    ? placeholder ?? '–'
    : format
      ? format(value)
      : value;
  const isPlaceholder = value === null || value === '';

  // Block 6.F.4 §3.1: refreshed visual affordance per peace-hub aesthetic
  // (cute/friendly + cream/sage palette). Replaces the old grey dotted-
  // underline with sage-300; adds sage-50 background tint on hover/focus
  // to surface the editable affordance more invitingly. The aria-label
  // is verb-led ("Edit phone — currently 555-1234") for natural screen-
  // reader speech per design plan §3.7 microcopy.
  return (
    <span className="inline-flex items-baseline gap-1">
      <button
        type="button"
        id={id}
        onClick={enterEditMode}
        aria-label={`Edit ${label} (currently ${value ?? 'empty'})`}
        className={
          'text-left border-b border-dotted border-sage-300 hover:border-sage-500 hover:bg-sage-50 rounded-sm px-0.5 -mx-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 ' +
          (multiline ? 'whitespace-pre-wrap ' : '') +
          (isPlaceholder ? 'text-ink-soft italic' : '')
        }
      >
        {displayValue}
      </button>
      <SavingIndicator state={saveState} errorMessage={saveError ?? undefined} />
    </span>
  );
}
